import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, Input, inject, ChangeDetectionStrategy } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { finalize, take } from 'rxjs';
import { EditPaymentDialogResponse } from 'src/app/dialogs/edit-payment-dialog/edit-payment-dialog.component';
import { TransactionDetail } from 'src/app/model/TransactionDetail';
import { AchService } from 'src/app/services/ach/ach.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { NLSTransactionService } from 'src/app/services/nls-transaction/nls-transaction.service';

@Component({
    selector: 'app-transaction-details-table',
    templateUrl: './transaction-details-table.component.html',
    styleUrl: './transaction-details-table.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TransactionDetailsTableComponent implements OnInit {
  /** Dependency injections */
  private _nlsTransactionService = inject(NLSTransactionService);
  private _dialogService = inject(DialogService);
  private _achService = inject(AchService);
  private _toastrService = inject(ToastrService);

  /** Input parameter - NLS transaction ID to fetch transaction details for */
  @Input() transaction_id!: number;

  /** Data for the table */
  transactionDetails: TransactionDetail[] = [];

  /** Loading state */
  loading = false;

  /** Error state */
  error: string | null = null;

  /** Table columns */
  displayedColumns: string[] = [
    'transaction_reference_no',
    'source',
    'transaction_amount',
    'effective_date',
    'actions',
  ];

  /**
   * On init, fetch transaction details if transaction_id is provided
   */
  ngOnInit(): void {
    if (this.transaction_id) {
      this.fetchTransactionDetails();
    } else {
      this.error = 'Transaction ID not found, cannot find transaction details';
    }
  }

  /**
   * Fetch transaction details for the current transaction ID
   */
  private fetchTransactionDetails(): void {
    this.loading = true;
    this.error = null;

    try {
      this._nlsTransactionService.fetchTransactionDetails(this.transaction_id).pipe(
        finalize(() => this.loading = false),
      ).subscribe({
        // On success, populate the table and sort
        next: (transactionDetails: TransactionDetail[]) => {
          this.transactionDetails = transactionDetails.sort((a, b) => {
            // Sort by pending payments first
            const aPending = a.source === 'Pending Payment' ? 1 : 0;
            const bPending = b.source === 'Pending Payment' ? 1 : 0;
            if (aPending !== bPending) return bPending - aPending;

            const aDate = a.effective_date ? new Date(a.effective_date).getTime() : 0;
            const bDate = b.effective_date ? new Date(b.effective_date).getTime() : 0;
            return bDate - aDate;
          });
        },
        error: (err: HttpErrorResponse) => {
          console.error('[TRANSACTION-DETAILS-TABLE]: error obtaining transaction details', err);
          this.error = err.error.error || 'Failed to load transaction details';
        }
      });
    } catch (err: any) {
      console.error('[TRANSACTION-DETAILS-TABLE]: error fetching transaction details', err);
      this.error = err.message || 'Failed to load transaction details';
      this.loading = false;
    }
  }

  /**
   * Format currency values
   * 
   * @param amount The amount to format
   * @return The formatted currency string
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  /**
   * Format date values as MM/DD/YYYY
   * 
   * @param date The date to format
   * @return The formatted date string
   */
  formatDate(date: Date | null): string {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date(date));
  }

  /**
   * Handle edit pending payment action
   * 
   * @param transactionDetail The transaction detail to edit
   */
  onEditPendingPayment(transactionDetail: TransactionDetail): void {
    // Find associated nls transaction
    const nlsTransaction = this._nlsTransactionService.getEntities()?.find(t => t.transaction_id === this.transaction_id);
    if (!nlsTransaction) {
      console.error('[PENDING-PAYMENTS-TABLE]: could not find associated NLS transaction for editing payment');
      this._toastrService.error('Could not find associated transaction for editing payment', 'Error');
      return;
    }

    this._dialogService.openEditPendingPaymentDialog(nlsTransaction, transactionDetail).pipe(
      take(1)
    ).subscribe({
      next: (result: EditPaymentDialogResponse | undefined) => {
        if (result?.success) {
          // Update the transaction detail in the table
          this.transactionDetails = this.transactionDetails.map(td =>
            td.transaction_reference_no === result.payment?.transaction_reference_no ? result.payment : td
          );
        }
      }
    });
  }

  /**
   * Handle delete pending payment action
   * 
   * @param transactionDetail The transaction detail to delete
   */
  onDeletePendingPayment(transactionDetail: TransactionDetail): void {
    this._dialogService.openConfirmationDialog({
      title: 'Delete Pending Payment',
      message: `Are you sure you want to delete the pending payment of ${this.formatCurrency(transactionDetail.transaction_amount)}? This action cannot be undone.`,
      confirm: {
        text: 'Delete',
        color: 'warn'
      },
    }).subscribe({
      // If confirmed, delete the pending payment
      next: (result: boolean) => {
        if (result) {
          this.deletePendingPayment(transactionDetail);
        }
      },
      error: (err: any) => {
        console.error('[TRANSACTION-DETAILS-TABLE]: error with confirmation dialog', err);
      }
    });
  }

  /**
   * Helper function to delete a pending payment
   * 
   * @param transactionDetail The transaction detail to delete
   * @param result 
   */
  private deletePendingPayment(transactionDetail: TransactionDetail): void {
    const loanNumber = transactionDetail.loan_number;
    const rowId = transactionDetail.transaction_reference_no; // backend RowID

    this._achService.deletePendingPayment$(loanNumber, rowId).subscribe({
      // Update table on success
      next: () => {
        this.transactionDetails = this.transactionDetails.filter(td => td.transaction_reference_no !== transactionDetail.transaction_reference_no);
        this._toastrService.success('Pending payment deleted successfully', 'Success');
      },
      // Handle error
      error: (err: any) => {
        console.error('[TRANSACTION-DETAILS-TABLE]: error deleting pending payment', err);
        this._toastrService.error('Failed to delete pending payment', 'Error');
      }
    });
  }
}
